import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "../../utils";
import { logger } from "@/lib/logger";
import type {
  Company_User_Access,
  Contact_User_Access,
  Contact_User_AccessInsertInput,
} from "@/src/gql/graphql";
import type { AccessLevel } from "@/lib/types/database";
import { isCompanyWideAccessLevel } from "@/lib/auth/access-levels";

/**
 * A single contact_user_access row joined with its contact, as returned by the
 * client-access queries. Keyed off the codegen row type so a schema change is a
 * compile-time error rather than a silent drift.
 */
export type ClientAccessRow = Pick<
  Contact_User_Access,
  | "id"
  | "user_id"
  | "contact_id"
  | "access_level"
  | "granted_at"
  | "expires_at"
  | "is_active"
> & {
  contact?: {
    id: string;
    name: string | null;
    email?: string | null;
    company_name?: string | null;
  } | null;
};

/**
 * A company_user_access row joined with its company — company-wide client portal
 * scope (all current + future contacts at the company).
 */
export type CompanyClientAccessRow = Pick<
  Company_User_Access,
  | "user_id"
  | "company_id"
  | "access_level"
  | "granted_at"
  | "expires_at"
  | "is_active"
> & {
  company?: {
    id: string;
    name: string | null;
  } | null;
};

const clientAccessQueryKey = (userId: string) =>
  ["admin", "users", userId, "client-access"] as const;

const companyClientAccessQueryKey = (userId: string) =>
  ["admin", "users", userId, "company-client-access"] as const;

export function invalidateClientAccessQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string
) {
  void queryClient.invalidateQueries({ queryKey: clientAccessQueryKey(userId) });
  void queryClient.invalidateQueries({
    queryKey: companyClientAccessQueryKey(userId),
  });
  void queryClient.invalidateQueries({ queryKey: ["user-access-grants", userId] });
  void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
}

/**
 * Company-wide grants for a CLIENT user (full / read_only → company_user_access).
 */
export function useCompanyClientAccess(userId: string | null | undefined) {
  return useQuery<CompanyClientAccessRow[]>({
    queryKey: companyClientAccessQueryKey(userId ?? ""),
    queryFn: async () => {
      if (!userId) return [];
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("company_user_access")
        .select("*, company:companies(id, name)")
        .eq("user_id", userId)
        .eq("is_active", true)
        .order("granted_at", { ascending: false });

      if (error) {
        logger.error("Error fetching company client access", error);
        throw new Error(error.message);
      }

      return data ?? [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The client (contact_user_access) grants a CLIENT user owns, newest first.
 * Used to render the existing-grants list + Revoke controls in the modal.
 */
export function useClientUserAccess(userId: string | null | undefined) {
  return useQuery<ClientAccessRow[]>({
    queryKey: clientAccessQueryKey(userId ?? ""),
    queryFn: async () => {
      if (!userId) return [];
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_user_access")
        .select("*, contact:contacts(id, name, email, company_name)")
        .eq("user_id", userId)
        .order("granted_at", { ascending: false });

      if (error) {
        logger.error("Error fetching client user access", error);
        throw new Error(error.message);
      }

      return data ?? [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGrantClientAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      contactId,
      accessLevel,
    }: {
      userId: string;
      contactId: string;
      accessLevel: AccessLevel;
    }) => {
      const supabase = getSupabase();

      // The access level decides the GRANT SHAPE:
      //   full / read_only -> COMPANY-WIDE (company_user_access row), so the
      //     client sees every contact at the company. access_level is persisted
      //     so the ticket-create gate (RLS) can distinguish full (can create)
      //     from read_only (cannot).
      //   limited          -> SINGLE CONTACT (contact_user_access row).
      const companyWide = isCompanyWideAccessLevel(accessLevel);

      if (companyWide) {
        // Resolve the contact's company so we can grant the whole company.
        const { data: contactRow, error: contactError } = await supabase
          .from("contacts")
          .select("company_id")
          .eq("id", contactId)
          .single();

        if (contactError) {
          logger.error("Error resolving contact company for grant", contactError);
          throw new Error("Failed to resolve the contact's company");
        }
        const companyId = (contactRow as { company_id: string | null } | null)
          ?.company_id;
        if (!companyId) {
          throw new Error(
            "This contact has no company, so company-wide access can't be granted. Choose Limited instead.",
          );
        }

        // Upsert the company-wide grant (primary key is user_id + company_id).
        const { data, error } = await supabase
          .from("company_user_access")
          .upsert(
            {
              user_id: userId,
              company_id: companyId,
              access_level: accessLevel,
              granted_at: new Date().toISOString(),
              is_active: true,
              expires_at: null,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id,company_id" },
          )
          .select()
          .single();

        if (error) {
          logger.error("Database operation failed (company grant)", error);
          throw new Error(error.message);
        }
        return data;
      }

      // limited -> per-contact grant.
      const { data: existing, error: existingError } = await supabase
        .from("contact_user_access")
        .select("*")
        .eq("user_id", userId)
        .eq("contact_id", contactId)
        .single();

      if (existingError && existingError.code !== "PGRST116") {
        logger.error("Error checking existing access", existingError);
        throw new Error("Failed to check existing access");
      }

      if (existing && existing.is_active) {
        throw new Error("User already has access to this contact");
      }

      let result;
      if (existing) {
        const { data, error } = await supabase
          .from("contact_user_access")
          .update({
            is_active: true,
            access_level: accessLevel,
            granted_at: new Date().toISOString(),
            expires_at: null,
          })
          .eq("id", existing.id)
          .select()
          .single();
        result = { data, error };
      } else {
        // Strongly-typed insert payload — every column maps to the verified
        // contact_user_access schema (id, user_id, contact_id, access_level,
        // granted_by, granted_at, expires_at, is_active).
        const insertPayload: Contact_User_AccessInsertInput = {
          user_id: userId,
          contact_id: contactId,
          access_level: accessLevel,
          granted_at: new Date().toISOString(),
          is_active: true,
        };
        const { data, error } = await supabase
          .from("contact_user_access")
          .insert(insertPayload)
          .select()
          .single();
        result = { data, error };
      }

      if (result.error) {
        logger.error("Database operation failed", result.error);
        throw new Error(result.error.message);
      }

      return result.data;
    },
    onSuccess: (_data, variables) => {
      invalidateClientAccessQueries(queryClient, variables.userId);
      toast.success("Client access granted successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to grant client access");
      logger.error("Error granting client access", error);
    },
  });
}

/**
 * Revoke a CLIENT user's access to a contact. Soft-revokes by setting
 * is_active = false (preserves the audit row + grant history) — falls back to
 * a hard delete only if no row matched the soft update.
 */
export function useRevokeClientAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      contactId,
    }: {
      userId: string;
      contactId: string;
    }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_user_access")
        .update({ is_active: false })
        .eq("user_id", userId)
        .eq("contact_id", contactId)
        .select();

      if (error) {
        logger.error("Error revoking client access", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      invalidateClientAccessQueries(queryClient, variables.userId);
      toast.success("Client access revoked successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to revoke client access");
      logger.error("Error revoking client access", error);
    },
  });
}

/**
 * Revoke a CLIENT user's company-wide access. Soft-revokes via is_active = false.
 */
export function useRevokeCompanyClientAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      companyId,
    }: {
      userId: string;
      companyId: string;
    }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("company_user_access")
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq("user_id", userId)
        .eq("company_id", companyId)
        .select();

      if (error) {
        logger.error("Error revoking company client access", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      invalidateClientAccessQueries(queryClient, variables.userId);
      toast.success("Company-wide access revoked successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to revoke company access");
      logger.error("Error revoking company client access", error);
    },
  });
}
